import { Box, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useDialogModal from "hooks/useDialogModal";
import FileUploadDialog from "./fileUploadDialog";
import { useCallback, useReducer } from "react";
import { formatDateWithPreferences } from "utils/dateFormatUtils";
import { DocumentService } from "service/documentService";
import { usePreferences } from "hooks/usePreferences";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import useAbortController from "hooks/useAbortController";
import validLookup from "utils/validLookup";
import { toast } from "react-toastify";

const isLocalDev = process.env.NODE_ENV === 'development';
const DEFAULT_PAGE_SIZE = 10;

// Column definitions for document table
const getDocumentColumns = (preferences) => [
  {
    Header: 'Document Type',
    accessor: 'documentType',
    Cell: ({ value }) => {
      const typeLabels = {
        'rx': 'RX / Prescription',
        'doctor_note': 'Doctor Note',
        'insurance': 'Insurance',
        'lab_result': 'Lab Result',
        'other': 'Other'
      };
      return typeLabels[value] || value;
    }
  },
  {
    Header: 'File Name',
    accessor: (row) => row.attachments?.[0]?.fileName || 'N/A',
  },
  {
    Header: 'Description',
    accessor: 'description',
    Cell: ({ value }) => value || '-'
  },
  {
    Header: 'Uploaded',
    accessor: 'createdAt',
    Cell: ({ value }) => formatDateWithPreferences(value, preferences)
  },
];

export default function CustomerDocuments({ customer }) {
    const [signal] = useAbortController();
    const { preferences } = usePreferences();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params }
    }, {
        documents: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
        previewOpen: false,
        previewUrl: null,
        previewFileName: '',
        previewType: '',
    });

    const [FileUpload, showFileUploadDialog] = useDialogModal(FileUploadDialog);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const customerId = customer?.customer?.id || customer?.id;

    // Fetch documents for customer
    const fetchData = useCallback(({ pageSize, pageIndex }) => {
        if (!customerId) return;

        DocumentService.listByCustomer(customerId, signal)
          .then(({ data }) => {
            const documents = Array.isArray(data) ? data : (data?.data || []);
            const pageCount = Math.ceil(documents.length / pageSize);
            const start = pageSize * pageIndex;
            const end = start + pageSize;
            const paginatedData = documents.slice(start, end);

            updateState({
                documents: paginatedData,
                totalRowCount: documents.length,
                pageCount,
            });
          })
          .catch(error => {
            if (isLocalDev) console.error('Error fetching documents:', error);
            toast.error('Failed to load documents');
          });
    }, [customerId, signal]);

    // Handle preview
    const handlePreview = (row) => {
        const attachment = row.attachments?.[0];
        if (!attachment) {
            toast.error('No file attached to this document');
            return;
        }

        DocumentService.download(attachment.id)
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            updateState({
                previewOpen: true,
                previewUrl: url,
                previewFileName: attachment.fileName || 'document',
                previewType: attachment.contentType || '',
            });
          })
          .catch(error => {
            if (isLocalDev) console.error('Preview failed:', error);
            toast.error('Failed to preview document');
          });
    };

    // Close preview dialog
    const handleClosePreview = () => {
        if (state.previewUrl) {
            window.URL.revokeObjectURL(state.previewUrl);
        }
        updateState({
            previewOpen: false,
            previewUrl: null,
            previewFileName: '',
            previewType: '',
        });
    };

    // Handle download
    const handleDownload = (row) => {
        const attachment = row.attachments?.[0];
        if (!attachment) {
            toast.error('No file attached to this document');
            return;
        }

        DocumentService.download(attachment.id)
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.fileName || 'document');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(error => {
            if (isLocalDev) console.error('Download failed:', error);
            toast.error('Failed to download document');
          });
    };

    // Handle delete from multi-select (array of rowIds)
    const handleDelete = useCallback((rowIds) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    for (const id of rowIds) {
                        await DocumentService.delete(parseInt(id, 10));
                    }
                    toast.success(`${rowIds.length} document(s) deleted`);
                    // Refresh the list
                    fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 });
                } catch (error) {
                    if (isLocalDev) console.error('Delete failed:', error);
                    toast.error('Failed to delete document(s)');
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete Document',
            message: `Are you sure you want to delete ${rowIds.length} document(s)?`,
        });
    }, [fetchData, showConfirmationDialog, closeConfirmationDialog]);

    // Handle delete from action button (single row)
    const handleDeleteRow = useCallback((row) => {
        handleDelete([row.id]);
    }, [handleDelete]);

    const handleUploadDocument = () => {
        showFileUploadDialog();
    };

    const handleFilesUpload = (files) => {
        if (files.length > 0 && customerId) {
            DocumentService.createBulk(customerId, files.map(f => ({
                file: f.file,
                documentType: f.documentType,
                description: f.notes
            })))
              .then(() => {
                toast.success(`${files.length} document(s) uploaded successfully`);
                // Refresh the list
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 });
              })
              .catch(err => {
                if (isLocalDev) console.error('Upload failed:', err);
                toast.error('Failed to upload document(s)');
              });
        } else if (!customerId) {
            toast.error('Customer ID is missing');
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Button 
                    variant="contained" 
                    onClick={handleUploadDocument}
                    sx={{ mb: 2 }}
                >
                    Upload Documents
                </Button>
            </Box>

            <EnhancedTable
                columns={getDocumentColumns(preferences)}
                data={state.documents}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                deleteRecordHandler={handleDelete}
                actions={[
                    {
                        icon: <VisibilityIcon />,
                        onClick: handlePreview,
                        tooltip: 'Preview'
                    },
                    {
                        icon: <DownloadIcon />,
                        onClick: handleDownload,
                        tooltip: 'Download'
                    },
                    {
                        icon: <DeleteForeverIcon />,
                        onClick: handleDeleteRow,
                        tooltip: 'Delete'
                    },
                ]}
            />

            <FileUpload onUploadDone={handleFilesUpload} />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} 
            />

            {/* Preview Dialog */}
            <Dialog 
                open={state.previewOpen} 
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Preview: {state.previewFileName}
                </DialogTitle>
                <DialogContent>
                    {state.previewUrl && (
                        <>
                            {/* Image preview */}
                            {state.previewType?.startsWith('image/') && (
                                <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    alignItems="center"
                                    sx={{ minHeight: 400 }}
                                >
                                    <img 
                                        src={state.previewUrl} 
                                        alt={state.previewFileName}
                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                    />
                                </Box>
                            )}
                            
                            {/* PDF preview */}
                            {state.previewType === 'application/pdf' && (
                                <iframe
                                    src={state.previewUrl}
                                    title={state.previewFileName}
                                    style={{ width: '100%', height: '70vh', border: 'none' }}
                                />
                            )}
                            
                            {/* Unsupported file type */}
                            {!state.previewType?.startsWith('image/') && state.previewType !== 'application/pdf' && (
                                <Box 
                                    display="flex" 
                                    flexDirection="column"
                                    justifyContent="center" 
                                    alignItems="center"
                                    sx={{ minHeight: 400, gap: 2 }}
                                >
                                    <p>Preview not available for this file type ({state.previewType})</p>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = state.previewUrl;
                                            link.download = state.previewFileName;
                                            link.click();
                                        }}
                                    >
                                        Download to View
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}